import { z } from 'zod';
import { ValidationError } from './errors';

/**
 * Result type for validation operations
 */
export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; error: ValidationError };

/**
 * Validates data against a Zod schema and returns a ValidationResult
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @returns ValidationResult with either the parsed data or validation error
 */
export function validateRequest<T>(
  schema: z.ZodType<T>,
  data: unknown
): ValidationResult<T> {
  const result = schema.safeParse(data);

  if (result.success) {
    return {
      success: true,
      data: result.data,
    };
  }

  return {
    success: false,
    error: ValidationError.fromZodError(result.error),
  };
}

/**
 * Validates response data against a Zod schema before sending to client
 * This is useful for ensuring API responses conform to expected schemas
 * @param schema - The Zod schema to validate against
 * @param data - The response data to validate
 * @returns ValidationResult with either the validated data or validation error
 */
export function validateResponse<T>(
  schema: z.ZodType<T>,
  data: unknown
): ValidationResult<T> {
  return validateRequest(schema, data);
}

/**
 * Validates data and throws a ValidationError if validation fails
 * Useful for scenarios where you want to handle errors via exception handling
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @returns The validated and parsed data
 * @throws ValidationError if validation fails
 */
export function validateOrThrow<T>(schema: z.ZodType<T>, data: unknown): T {
  const result = validateRequest(schema, data);

  if (!result.success) {
    throw result.error;
  }

  return result.data;
}

/**
 * Creates a validator function that can be reused with the same schema
 * @param schema - The Zod schema to create a validator for
 * @returns A function that validates data against the provided schema
 */
export function createValidator<T>(
  schema: z.ZodType<T>
): (data: unknown) => ValidationResult<T> {
  return (data: unknown) => validateRequest(schema, data);
}

/**
 * Creates a validator function that throws on validation failure
 * @param schema - The Zod schema to create a validator for
 * @returns A function that validates data and throws ValidationError on failure
 */
export function createValidatorOrThrow<T>(
  schema: z.ZodType<T>
): (data: unknown) => T {
  return (data: unknown) => validateOrThrow(schema, data);
}

/**
 * Validates multiple pieces of data against their respective schemas
 * Returns success only if all validations pass
 * @param validations - Array of [schema, data] tuples to validate
 * @returns ValidationResult with array of validated data or first validation error
 */
export function validateMultiple(
  ...validations: Array<[schema: z.ZodType<any>, data: unknown]>
): ValidationResult<any[]> {
  const results: any[] = [];

  for (const [schema, data] of validations) {
    const result = validateRequest(schema, data);
    if (!result.success) {
      return result;
    }
    results.push(result.data);
  }

  return {
    success: true,
    data: results,
  };
}

/**
 * Validates an array of items against a schema
 * @param schema - The Zod schema for individual items
 * @param items - Array of items to validate
 * @returns ValidationResult with validated array or validation error
 */
export function validateArray<T>(
  schema: z.ZodType<T>,
  items: unknown[]
): ValidationResult<T[]> {
  return validateRequest(z.array(schema), items);
}

/**
 * Validates partial data against a schema (makes all properties optional)
 * Useful for PATCH operations where only some fields are provided
 * @param schema - The base Zod schema
 * @param data - The partial data to validate
 * @returns ValidationResult with validated partial data or validation error
 */
export function validatePartial<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
  data: unknown
): ValidationResult<Partial<z.infer<z.ZodObject<T>>>> {
  return validateRequest(schema.partial(), data) as ValidationResult<
    Partial<z.infer<z.ZodObject<T>>>
  >;
}

/**
 * Utility type for extracting the inferred type from a Zod schema
 */
export type InferSchema<T extends z.ZodType<any>> = z.infer<T>;

/**
 * Common validation schemas that can be reused across the application
 */
export const commonSchemas = {
  /**
   * Schema for MongoDB ObjectId strings
   */
  objectId: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ObjectId format'),

  /**
   * Schema for email addresses
   */
  email: z.string().email('Invalid email format'),

  /**
   * Schema for non-empty strings
   */
  nonEmptyString: z.string().min(1, 'String cannot be empty'),

  /**
   * Schema for positive integers
   */
  positiveInt: z.number().int().positive('Must be a positive integer'),

  /**
   * Schema for non-negative integers (including zero)
   */
  nonNegativeInt: z.number().int().min(0, 'Must be a non-negative integer'),

  /**
   * Schema for URL strings
   */
  url: z.string().url('Invalid URL format'),

  /**
   * Schema for UUID strings
   */
  uuid: z.string().uuid('Invalid UUID format'),

  /**
   * Schema for ISO date strings
   */
  isoDate: z.string().datetime('Invalid ISO date format'),

  /**
   * Schema for pagination parameters
   */
  pagination: z.object({
    page: z.number().int().min(1, 'Page must be at least 1').default(1),
    limit: z
      .number()
      .int()
      .min(1, 'Limit must be at least 1')
      .max(100, 'Limit cannot exceed 100')
      .default(10),
  }),

  /**
   * Schema for search parameters
   */
  search: z.object({
    query: z.string().min(1, 'Search query cannot be empty').optional(),
    sort: z.enum(['asc', 'desc']).default('desc'),
    sortBy: z.string().default('createdAt'),
  }),
} as const;
